/**
 * The conversation with a forge: turn history, the live turn's stream, and
 * the screenshots + tool events it produces.
 *
 * NOT React Query: this is the WebSocket-driven part (docs/react-query-patterns.md
 * "Do NOT use React Query for"). Sending a turn is `POST /turns` → the
 * response's `requestId` → `useStreaming` subscribes; the runtime's events
 * come back as `token` / `activity` / `tool_call` / `tool_result` /
 * `forge_screenshot` / `forge_question` / `error` / `complete` frames on that id.
 *
 * A `forge_question` means Claude asked the owner (AskUserQuestion) and the
 * turn is waiting: `question` holds it until `answerQuestion` sends the
 * answer, and Claude then carries on in the same turn. A `forge_plan` is the
 * same for a plan (ExitPlanMode, ADR-023 A14): `plan` holds it until the
 * owner approves it or sends it back. A `forge_art` is a batch of art over the
 * owner's threshold (ADR-037 D5): `artBatch` holds it until the owner approves
 * or declines it, and the art tool carries on in the same turn.
 *
 * Only the prompt is persisted server-side (forge_turns has no transcript),
 * so history renders each prior turn as the user's prompt plus a one-line
 * outcome bubble; the streamed assistant text lives for the page session.
 *
 * Clear (ADR-023 A21) starts a new conversation: `messages` is the
 * conversation Claude remembers and `earlierMessages` what came before it —
 * everything while a Clear waits for the next message, else everything before
 * the latest turn that started a new conversation. Compact is a turn whose
 * prompt is Claude Code's `/compact`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { apiErrorCode } from '@/lib/api-error-code';
import { authApi } from '@/lib/auth-api';
import { useStreaming } from '@/lib/hooks/useStreaming';
import type {
  ForgeArtEventPayload,
  ForgePlanEventPayload,
  ForgeQuestionEventPayload,
} from '@/lib/websocket';
import { addBreadcrumb, captureError } from '@/lib/telemetry';
import { formatCredits } from '@/lib/format-credits';
import { forgeQueryKeys } from '../api/queryKeys';
import type {
  ForgeMessage,
  ForgePendingArtBatch,
  ForgePendingPlan,
  ForgePendingQuestion,
  ForgeScreenshot,
  ForgeToolEvent,
  ForgeTurnSummary,
} from '../types';

/** Screenshots kept per page session — older ones scroll off. */
const MAX_SCREENSHOTS = 24;
/** A turn's tool events kept in memory (Claude Code turns can run hundreds). */
const MAX_TOOL_EVENTS_PER_TURN = 400;

/** The prompt of a Compact turn: Claude Code's own command (ADR-023 A21). */
export const FORGE_COMPACT_PROMPT = '/compact';

/** Whether a prompt asks Claude Code to compact the conversation. */
export function isCompactPrompt(prompt: string): boolean {
  return /^\/compact(\s|$)/.test(prompt.trim());
}

/**
 * What became of a message handed to `sendTurn`. `not_running`: the platform
 * stopped the forge since the page last looked (idle, hours used up) — the
 * message is not in the conversation, for the page to start the forge and
 * send it.
 */
export type ForgeSendOutcome = 'sent' | 'not_running' | 'failed' | 'skipped';

export interface UseForgeTurnOptions {
  /** The owner pressed Clear and no turn has started the new conversation yet. */
  newConversationPending?: boolean;
}

export interface UseForgeTurnResult {
  /** The conversation Claude remembers (since the latest Clear). */
  messages: ForgeMessage[];
  /** What came before the latest Clear; Claude does not see it. */
  earlierMessages: ForgeMessage[];
  isProcessing: boolean;
  streamingText: string;
  activity: string | null;
  screenshots: ForgeScreenshot[];
  /** Screenshots that arrived since the user last opened the viewer. */
  unseenScreenshots: number;
  /** Tool events of the live turn. */
  pendingToolEvents: ForgeToolEvent[];
  /** Tool events of completed turns, keyed by the assistant bubble's timestamp. */
  toolEventHistory: Record<string, ForgeToolEvent[]>;
  lastFailure: { code: string; message: string; retryable: boolean } | null;
  /** The question Claude is waiting on, if any. */
  question: ForgePendingQuestion | null;
  /** Send the owner's answers (question text → answer). Throws if the platform refuses them. */
  answerQuestion: (answers: Record<string, string>) => Promise<void>;
  /** The plan Claude is waiting for the owner to approve, if any. */
  plan: ForgePendingPlan | null;
  /** Approve the plan: Claude leaves plan mode and builds. Throws if the platform refuses. */
  approvePlan: () => Promise<void>;
  /** Send the plan back with what to change: Claude keeps planning. */
  revisePlan: (feedback: string) => Promise<void>;
  /** A batch of art waiting for the owner's approval, if any (ADR-037 D5). */
  artBatch: ForgePendingArtBatch | null;
  /** Approve or decline it. Throws if the platform refuses. */
  decideArt: (approved: boolean) => Promise<void>;
  /** Open a turn. Claude decides for itself whether to plan first (ADR-023 A21). */
  sendTurn: (prompt: string) => Promise<ForgeSendOutcome>;
  stopTurn: () => Promise<void>;
  retry: () => Promise<void>;
  clearScreenshots: () => void;
  markScreenshotsSeen: () => void;
}

/** The image type of a screenshot, from its file name (the runtime sends PNG, JPEG or WebP). */
export function screenshotMimeType(path: string): string {
  if (/\.jpe?g$/i.test(path)) return 'image/jpeg';
  if (/\.webp$/i.test(path)) return 'image/webp';
  return 'image/png';
}

/** How an answered question reads in the conversation. */
export function formatAnswers(
  question: ForgePendingQuestion,
  answers: Record<string, string>,
): string {
  return question.questions
    .map((q) => `${q.header || q.question}: ${answers[q.question] ?? ''}`)
    .join('\n');
}

/** How the owner's decision on a batch of art reads in the conversation. */
export function formatArtDecision(batch: ForgePendingArtBatch['batch'], approved: boolean): string {
  const n = batch.items.length;
  const what = `${n} picture${n === 1 ? '' : 's'}, about ${formatCredits(batch.estimateCredits)} credits`;
  return approved ? `Approved the art (${what}).` : `Declined the art (${what}).`;
}

/** How the owner's decision on a plan reads in the conversation. */
export function formatPlanDecision(approved: boolean, feedback: string): string {
  if (approved) return 'Approved the plan.';
  return feedback ? `Keep planning: ${feedback}` : 'Keep planning.';
}

function outcomeLine(turn: ForgeTurnSummary): string {
  // costCents is already in credits (1 credit = 1 cent).
  const cost = turn.costCents > 0 ? ` · ${formatCredits(turn.costCents)} credits` : '';
  const tools = turn.toolCalls > 0 ? ` · ${turn.toolCalls} tool calls` : '';
  if (isCompactPrompt(turn.prompt) && turn.status === 'completed') {
    return `Compacted the conversation${cost}.`;
  }
  switch (turn.status) {
    case 'completed':
      return `Turn completed${tools}${cost}.`;
    case 'stopped':
      return `Turn stopped${tools}${cost}.`;
    case 'failed':
      return `Turn failed${tools}${cost}.`;
    case 'expired':
      return `Turn expired (hit the wall-clock limit)${tools}${cost}.`;
    default:
      return 'Turn in progress…';
  }
}

export function useForgeTurn(
  forgeId: string | null,
  options: UseForgeTurnOptions = {},
): UseForgeTurnResult {
  const newConversationPending = options.newConversationPending === true;
  const queryClient = useQueryClient();
  const authToken = authApi.getIdToken();

  const [requestId, setRequestId] = useState<string | null>(null);
  const [liveMessages, setLiveMessages] = useState<ForgeMessage[]>([]);
  const [activity, setActivity] = useState<string | null>(null);
  const [screenshots, setScreenshots] = useState<ForgeScreenshot[]>([]);
  const [unseenScreenshots, setUnseenScreenshots] = useState(0);
  const screenshotSeqRef = useRef(0);
  const [pendingToolEvents, setPendingToolEvents] = useState<ForgeToolEvent[]>([]);
  const [toolEventHistory, setToolEventHistory] = useState<Record<string, ForgeToolEvent[]>>({});
  const [lastFailure, setLastFailure] = useState<UseForgeTurnResult['lastFailure']>(null);
  const [isSending, setIsSending] = useState(false);
  const [question, setQuestion] = useState<ForgePendingQuestion | null>(null);
  const [plan, setPlan] = useState<ForgePendingPlan | null>(null);
  const [artBatch, setArtBatch] = useState<ForgePendingArtBatch | null>(null);
  // Questions and plans answered (or dismissed by the turn ending) in this page
  // session: the turns query keeps reporting one as open until it refetches.
  const settledQuestionIdsRef = useRef<Set<string>>(new Set());
  // Characters of the live turn's stream already moved into a bubble (the
  // reply up to a question); the stream itself only resets between turns.
  const [streamOffset, setStreamOffset] = useState(0);
  const streamedCharsRef = useRef(0);
  // The user bubble (by timestamp) of the turn this page session sent that
  // started a new conversation. Set when it is sent, so the bubble never
  // folds away while the open is in flight.
  const [liveConversationStart, setLiveConversationStart] = useState<string | null>(null);
  const newConversationPendingRef = useRef(newConversationPending);
  newConversationPendingRef.current = newConversationPending;

  const streamingTextRef = useRef('');
  const lastPromptRef = useRef<string | null>(null);
  // toolUseId → tool name, so a result bubble can be named (forge results
  // carry only the id; see websocket.ts ToolResultMessage).
  const toolNamesRef = useRef<Map<string, string>>(new Map());
  // Turns whose requestId we have already adopted from history (page reload
  // mid-turn) — never re-subscribe to the same one twice.
  const adoptedOpenTurnRef = useRef<string | null>(null);
  // Turns THIS page session opened. The reload pick-up below must never adopt
  // one: right after `complete` clears `requestId`, the turns query still says
  // `open` until its refetch lands, and adopting it re-subscribed to a stream
  // that had already ended — the page then showed the turn as running forever
  // (stop button, build and deploy disabled) with the streamed reply twice.
  const ownRequestIdsRef = useRef<Set<string>>(new Set());

  const turnsQuery = useQuery({
    queryKey: forgeQueryKeys.turns(forgeId ?? ''),
    queryFn: () => api.listForgeTurns(forgeId!),
    enabled: !!forgeId,
  });
  const turns = turnsQuery.data?.turns;

  // Reload mid-turn: an `open` turn in history is still streaming — subscribe
  // to its requestId so the page picks the stream back up. Its prompt moves
  // into the live conversation, as if sent from this page, so what streams
  // next (a reply, an answered question) follows it rather than landing
  // below the history's outcome line once the turn closes.
  useEffect(() => {
    if (!turns || requestId) return;
    const open = turns.find((t) => t.status === 'open');
    if (
      open &&
      adoptedOpenTurnRef.current !== open.requestId &&
      !ownRequestIdsRef.current.has(open.requestId)
    ) {
      adoptedOpenTurnRef.current = open.requestId;
      streamingTextRef.current = '';
      setLiveMessages((prev) =>
        prev.some((m) => m.role === 'user' && m.content === open.prompt)
          ? prev
          : [
              ...prev,
              {
                role: 'user',
                content: open.prompt,
                timestamp: open.startedAt ?? new Date().toISOString(),
              },
            ],
      );
      setRequestId(open.requestId);
    }
  }, [turns, requestId]);

  // Reload while Claude waits on a question, a plan or art: history carries it.
  useEffect(() => {
    if (!turns || question || plan || artBatch) return;
    const open = turns.find((t) => t.status === 'open' && t.openQuestion);
    const waiting = open?.openQuestion;
    if (!open || !waiting || settledQuestionIdsRef.current.has(waiting.questionId)) return;
    if (waiting.kind === 'plan') {
      setPlan({ turnId: open.id, questionId: waiting.questionId, plan: waiting.plan });
    } else if (waiting.kind === 'art') {
      setArtBatch({ turnId: open.id, questionId: waiting.questionId, batch: waiting.batch });
    } else {
      setQuestion({
        turnId: open.id,
        questionId: waiting.questionId,
        questions: waiting.questions,
      });
    }
  }, [turns, question, plan, artBatch]);

  const finishTurn = useCallback(
    (outcome: { failed?: { code: string; message: string } }) => {
      setActivity(null);
      setQuestion((q) => {
        if (q) settledQuestionIdsRef.current.add(q.questionId);
        return null;
      });
      setPlan((p) => {
        if (p) settledQuestionIdsRef.current.add(p.questionId);
        return null;
      });
      setArtBatch((a) => {
        if (a) settledQuestionIdsRef.current.add(a.questionId);
        return null;
      });
      streamedCharsRef.current = 0;
      setStreamOffset(0);
      const stamp = new Date().toISOString();
      const text = streamingTextRef.current.trim();
      streamingTextRef.current = '';
      setPendingToolEvents((events) => {
        if (events.length > 0) setToolEventHistory((h) => ({ ...h, [stamp]: events }));
        return [];
      });
      if (text || outcome.failed) {
        setLiveMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: text || `Turn failed: ${outcome.failed?.message ?? 'unknown error'}`,
            timestamp: stamp,
          },
        ]);
      }
      setRequestId(null);
      setIsSending(false);
      // The turn row (status/cost/toolCalls) and the forge (lastActiveAt,
      // pendingCommand) both moved server-side.
      if (forgeId) {
        void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.detail(forgeId) });
      }
    },
    [forgeId, queryClient],
  );

  // Claude stopped to ask the owner something: what it said before asking
  // becomes its own bubble, so the conversation reads: reply so far →
  // question (or plan) → the owner's reply → the rest.
  const moveStreamToBubble = useCallback(() => {
    setActivity(null);
    const said = streamingTextRef.current.trim();
    if (said) {
      const stamp = new Date().toISOString();
      setLiveMessages((prev) => [...prev, { role: 'assistant', content: said, timestamp: stamp }]);
    }
    streamingTextRef.current = '';
    setStreamOffset(streamedCharsRef.current);
  }, []);

  const streaming = useStreaming({
    requestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!authToken,
    // The activity line says what is happening while nothing else shows
    // (starting up, compacting, retrying a failed model call). Output means
    // it no longer applies, so a "retrying" line never outlives the recovery.
    onTextUpdate: useCallback((_nodeId: string, text: string) => {
      streamingTextRef.current += text;
      streamedCharsRef.current += text.length;
      setActivity(null);
    }, []),
    onActivity: useCallback((label: string) => setActivity(label), []),
    onToolCall: useCallback((toolName: string, toolInput: unknown, toolUseId?: string) => {
      setActivity(null);
      if (toolUseId) toolNamesRef.current.set(toolUseId, toolName);
      setPendingToolEvents((events) =>
        [
          ...events,
          {
            type: 'tool_call' as const,
            timestamp: new Date().toISOString(),
            data: { toolName, toolInput, toolUseId },
          },
        ].slice(-MAX_TOOL_EVENTS_PER_TURN),
      );
    }, []),
    onToolResult: useCallback(
      (toolName: string, toolResult: unknown, isError: boolean, toolUseId?: string) => {
        const name = toolName || (toolUseId ? toolNamesRef.current.get(toolUseId) : undefined);
        setPendingToolEvents((events) =>
          [
            ...events,
            {
              type: 'tool_result' as const,
              timestamp: new Date().toISOString(),
              data: { toolName: name ?? 'tool', toolResult, isError, toolUseId },
            },
          ].slice(-MAX_TOOL_EVENTS_PER_TURN),
        );
      },
      [],
    ),
    onForgeScreenshot: useCallback(
      (payload: { requestId: string; path: string; contentBase64: string }) => {
        screenshotSeqRef.current += 1;
        const id = `shot-${screenshotSeqRef.current}`;
        setScreenshots((prev) =>
          [
            ...prev,
            {
              id,
              path: payload.path,
              dataUrl: `data:${screenshotMimeType(payload.path)};base64,${payload.contentBase64}`,
              requestId: payload.requestId,
              receivedAt: new Date().toISOString(),
            },
          ].slice(-MAX_SCREENSHOTS),
        );
        setUnseenScreenshots((n) => Math.min(n + 1, MAX_SCREENSHOTS));
      },
      [],
    ),
    onForgeQuestion: useCallback(
      (payload: ForgeQuestionEventPayload) => {
        if (settledQuestionIdsRef.current.has(payload.questionId)) return;
        moveStreamToBubble();
        setQuestion({
          turnId: payload.turnId,
          questionId: payload.questionId,
          questions: payload.questions,
        });
      },
      [moveStreamToBubble],
    ),
    onForgePlan: useCallback(
      (payload: ForgePlanEventPayload) => {
        if (settledQuestionIdsRef.current.has(payload.questionId)) return;
        moveStreamToBubble();
        setPlan({ turnId: payload.turnId, questionId: payload.questionId, plan: payload.plan });
      },
      [moveStreamToBubble],
    ),
    onForgeArt: useCallback(
      (payload: ForgeArtEventPayload) => {
        if (settledQuestionIdsRef.current.has(payload.questionId)) return;
        moveStreamToBubble();
        setArtBatch({
          turnId: payload.turnId,
          questionId: payload.questionId,
          batch: payload.batch,
        });
      },
      [moveStreamToBubble],
    ),
    onError: useCallback(
      (code: string, message: string) => {
        captureError(new Error(message), { code, forgeId, surface: 'forge-turn' });
        // Transport-level failures are retryable by resending the same prompt;
        // a turn the platform closed as failed is not (the runtime already ran).
        const retryable = code !== 'FORGE_TURN_FAILED';
        setLastFailure({ code, message, retryable });
        if (!retryable) finishTurn({ failed: { code, message } });
      },
      [finishTurn, forgeId],
    ),
    onComplete: useCallback(() => {
      finishTurn({});
    }, [finishTurn]),
  });

  const sendTurn = useCallback(
    async (prompt: string): Promise<ForgeSendOutcome> => {
      if (!forgeId || isSending || requestId) return 'skipped';
      const trimmed = prompt.trim();
      if (!trimmed) return 'skipped';
      setIsSending(true);
      setLastFailure(null);
      lastPromptRef.current = trimmed;
      streamingTextRef.current = '';
      streamedCharsRef.current = 0;
      setStreamOffset(0);
      streaming.reset();
      const stamp = new Date().toISOString();
      const startsNewConversation = newConversationPendingRef.current;
      const previousStart = liveConversationStart;
      if (startsNewConversation) setLiveConversationStart(stamp);
      setLiveMessages((prev) => [...prev, { role: 'user', content: trimmed, timestamp: stamp }]);
      try {
        const result = await api.sendForgeTurn(forgeId, { prompt: trimmed });
        addBreadcrumb('forge', 'turn opened', {
          forgeId,
          turnId: result.turnId,
          newConversation: result.turn.newConversation === true,
        });
        // The server decides which turn honours a Clear; follow it.
        if (result.turn.newConversation === true) setLiveConversationStart(stamp);
        else if (startsNewConversation) setLiveConversationStart(previousStart);
        ownRequestIdsRef.current.add(result.requestId);
        setRequestId(result.requestId);
        void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.detail(forgeId) });
        return 'sent';
      } catch (err) {
        if (startsNewConversation) setLiveConversationStart(previousStart);
        if (apiErrorCode(err) === 'FORGE_NOT_RUNNING') {
          // The platform stopped the forge since the page last looked. The
          // message leaves the conversation; the page starts the forge and
          // sends it again.
          setLiveMessages((prev) =>
            prev.filter((m) => !(m.role === 'user' && m.timestamp === stamp)),
          );
          setIsSending(false);
          void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.detail(forgeId) });
          return 'not_running';
        }
        const message = err instanceof Error ? err.message : 'Failed to send';
        setLastFailure({ code: 'SEND_FAILED', message, retryable: true });
        setIsSending(false);
        return 'failed';
      }
    },
    [forgeId, isSending, requestId, streaming, queryClient, liveConversationStart],
  );

  const answerQuestion = useCallback(
    async (answers: Record<string, string>) => {
      if (!forgeId || !question) return;
      await api.answerForgeQuestion(forgeId, question.turnId, {
        questionId: question.questionId,
        answers,
      });
      addBreadcrumb('forge', 'question answered', { forgeId, turnId: question.turnId });
      settledQuestionIdsRef.current.add(question.questionId);
      setQuestion(null);
      setLiveMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: formatAnswers(question, answers),
          timestamp: new Date().toISOString(),
        },
      ]);
    },
    [forgeId, question],
  );

  const decidePlan = useCallback(
    async (approved: boolean, feedback: string) => {
      if (!forgeId || !plan) return;
      await api.decideForgePlan(forgeId, plan.turnId, {
        questionId: plan.questionId,
        approved,
        ...(approved || !feedback ? {} : { feedback }),
      });
      addBreadcrumb('forge', approved ? 'plan approved' : 'plan sent back', {
        forgeId,
        turnId: plan.turnId,
      });
      settledQuestionIdsRef.current.add(plan.questionId);
      setPlan(null);
      // The plan stays in the conversation, followed by the decision.
      const stamp = new Date().toISOString();
      setLiveMessages((prev) => [
        ...prev,
        { role: 'assistant', content: plan.plan, timestamp: stamp },
        { role: 'user', content: formatPlanDecision(approved, feedback), timestamp: stamp },
      ]);
    },
    [forgeId, plan],
  );
  const approvePlan = useCallback(() => decidePlan(true, ''), [decidePlan]);

  const decideArt = useCallback(
    async (approved: boolean) => {
      if (!forgeId || !artBatch) return;
      await api.decideForgeArtBatch(forgeId, artBatch.turnId, {
        questionId: artBatch.questionId,
        approved,
      });
      addBreadcrumb('forge', approved ? 'art approved' : 'art declined', {
        forgeId,
        turnId: artBatch.turnId,
        estimateCredits: artBatch.batch.estimateCredits,
      });
      settledQuestionIdsRef.current.add(artBatch.questionId);
      setArtBatch(null);
      setLiveMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: formatArtDecision(artBatch.batch, approved),
          timestamp: new Date().toISOString(),
        },
      ]);
      void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.art(forgeId) });
    },
    [forgeId, artBatch, queryClient],
  );
  const revisePlan = useCallback((feedback: string) => decidePlan(false, feedback), [decidePlan]);

  const stopTurn = useCallback(async () => {
    if (!forgeId) return;
    try {
      await api.stopForgeTurn(forgeId);
      addBreadcrumb('forge', 'stop requested', { forgeId });
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        forgeId,
        surface: 'forge-stop-turn',
      });
    }
  }, [forgeId]);

  const retry = useCallback(async () => {
    const prompt = lastPromptRef.current;
    if (!prompt) return;
    // Drop the optimistic user bubble the failed send left behind.
    setLiveMessages((prev) => {
      const last = prev[prev.length - 1];
      return last?.role === 'user' && last.content === prompt ? prev.slice(0, -1) : prev;
    });
    setIsSending(false);
    await sendTurn(prompt);
  }, [sendTurn]);

  const clearScreenshots = useCallback(() => {
    setScreenshots([]);
    setUnseenScreenshots(0);
  }, []);
  const markScreenshotsSeen = useCallback(() => setUnseenScreenshots(0), []);

  // History bubbles: every persisted turn as prompt + outcome, EXCEPT the one
  // currently open (its prompt is already in liveMessages, or it is being
  // picked up from a reload in which case there is no live bubble yet).
  const historyMessages = useMemo<ForgeMessage[]>(() => {
    if (!turns) return [];
    const liveStamps = new Set(liveMessages.map((m) => m.content));
    return turns
      .filter((t) => t.status !== 'open' || !liveStamps.has(t.prompt))
      .flatMap((t) => {
        const started = t.startedAt ?? new Date(0).toISOString();
        const out: ForgeMessage[] = [{ role: 'user', content: t.prompt, timestamp: started }];
        if (t.status !== 'open') {
          out.push({ role: 'assistant', content: outcomeLine(t), timestamp: t.endedAt ?? started });
        }
        return out;
      });
  }, [turns, liveMessages]);

  const allMessages = useMemo(() => {
    // History is ascending by start; live bubbles come after.
    const history = [...historyMessages].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    // A turn opened in this page session is in BOTH lists once the turns
    // query refetches — keep the live copy (it carries the streamed text).
    const liveUserPrompts = new Set(
      liveMessages.filter((m) => m.role === 'user').map((m) => m.content),
    );
    const dedupedHistory: ForgeMessage[] = [];
    for (let i = 0; i < history.length; i += 1) {
      const m = history[i];
      if (m.role === 'user' && liveUserPrompts.has(m.content)) {
        // Skip this prompt and its outcome line.
        if (history[i + 1]?.role === 'assistant') i += 1;
        continue;
      }
      dedupedHistory.push(m);
    }
    return [...dedupedHistory, ...liveMessages];
  }, [historyMessages, liveMessages]);

  const isProcessing = isSending || requestId !== null;

  // Where the conversation Claude remembers begins (ADR-023 A21). A Clear
  // waiting for the next message folds everything — except while a turn is
  // being opened or runs: that turn is the one consuming it.
  const conversationStart = useMemo(() => {
    const userBubbleAt = (stamp: string) => {
      for (let i = allMessages.length - 1; i >= 0; i -= 1) {
        if (allMessages[i].role === 'user' && allMessages[i].timestamp === stamp) return i;
      }
      return -1;
    };
    if (newConversationPending && !isProcessing) return allMessages.length;
    if (liveConversationStart) {
      const i = userBubbleAt(liveConversationStart);
      if (i >= 0) return i;
    }
    const started = (turns ?? [])
      .filter((t) => t.newConversation === true && t.startedAt)
      .map((t) => t.startedAt as string)
      .sort();
    const latest = started[started.length - 1];
    if (latest) {
      const i = userBubbleAt(latest);
      if (i >= 0) return i;
    }
    return 0;
  }, [allMessages, newConversationPending, isProcessing, liveConversationStart, turns]);

  const messages = useMemo(
    () => allMessages.slice(conversationStart),
    [allMessages, conversationStart],
  );
  const earlierMessages = useMemo(
    () => allMessages.slice(0, conversationStart),
    [allMessages, conversationStart],
  );

  const streamingText = (streaming.streamingText.forge ?? '').slice(streamOffset);

  return {
    messages,
    earlierMessages,
    isProcessing,
    streamingText,
    activity,
    screenshots,
    unseenScreenshots,
    pendingToolEvents,
    toolEventHistory,
    lastFailure,
    question,
    answerQuestion,
    plan,
    approvePlan,
    revisePlan,
    artBatch,
    decideArt,
    sendTurn,
    stopTurn,
    retry,
    clearScreenshots,
    markScreenshotsSeen,
  };
}
